import { supabase } from '../lib/supabaseClient';

/**
 * License service for managing machine registrations, cooldowns, and seat limits
 */

// Get machine seat limit based on subscription plan
export const getSeatLimit = (planType) => {
    const limits = {
        'free': 1,
        'pro': 2,
        'studio': 5,
        'enterprise': 10
    };
    return limits[planType?.toLowerCase()] || 1;
};

// Check if user can activate on this machine
export const canActivateMachine = async (userId, machineId, planType = 'free') => {
    try {
        const seatLimit = getSeatLimit(planType);

        // Check current registered machines
        const { data: machines, error: machinesError } = await supabase
            .from('user_machines')
            .select('*')
            .eq('user_id', userId);

        if (machinesError) throw machinesError;

        // Check if this machine is already registered
        const existingMachine = machines?.find(m => m.machine_id === machineId);
        if (existingMachine) {
            // Update last_used_at and allow access
            await supabase
                .from('user_machines')
                .update({ last_used_at: new Date().toISOString() })
                .eq('id', existingMachine.id);

            return { allowed: true, reason: 'Machine already registered' };
        }

        // Check if under seat limit
        if ((machines?.length || 0) >= seatLimit) {
            return {
                allowed: false,
                reason: `You've reached your device limit (${seatLimit}). Upgrade your plan or deactivate another device.`,
                machines: machines
            };
        }

        // Check for active cooldowns on other machines
        const { data: cooldowns } = await supabase
            .from('machine_cooldowns')
            .select('*')
            .eq('user_id', userId)
            .gt('cooldown_ends_at', new Date().toISOString());

        if (cooldowns && cooldowns.length > 0) {
            const activeCooldown = cooldowns[0];
            const endsAt = new Date(activeCooldown.cooldown_ends_at);
            const hoursLeft = Math.ceil((endsAt - new Date()) / (1000 * 60 * 60));

            return {
                allowed: false,
                reason: `Your license is active on another device. Please wait ${hoursLeft} hour(s) or upgrade.`,
                cooldownEndsAt: endsAt
            };
        }

        // Check daily sign-in limit (max 3 different machines per day)
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const { data: todayMachines } = await supabase
            .from('user_machines')
            .select('*')
            .eq('user_id', userId)
            .gte('activated_at', today.toISOString());

        if ((todayMachines?.length || 0) >= 3) {
            return {
                allowed: false,
                reason: 'You\'ve activated too many devices today. Please try again tomorrow.'
            };
        }

        // All checks passed - can activate
        return { allowed: true, reason: 'Can activate on this machine' };

    } catch (error) {
        console.error('Error checking machine activation:', error);
        return { allowed: false, reason: 'Error checking license status' };
    }
};

// Register a new machine
export const registerMachine = async (userId, machineId, machineName, os) => {
    try {
        const { data, error } = await supabase
            .from('user_machines')
            .insert({
                user_id: userId,
                machine_id: machineId,
                machine_name: machineName,
                os: os
            })
            .select()
            .single();

        if (error) throw error;
        return { success: true, machine: data };

    } catch (error) {
        console.error('Error registering machine:', error);
        return { success: false, error: error.message };
    }
};

// Deactivate a machine (with monthly limit check)
export const deactivateMachine = async (userId, machineId) => {
    try {
        // Check monthly deactivation limit (max 2 per month)
        const monthStart = new Date();
        monthStart.setDate(1);
        monthStart.setHours(0, 0, 0, 0);

        const { data: deactivations } = await supabase
            .from('machine_deactivations')
            .select('*')
            .eq('user_id', userId)
            .gte('deactivated_at', monthStart.toISOString());

        if ((deactivations?.length || 0) >= 2) {
            return {
                success: false,
                error: 'You\'ve reached your monthly device deactivation limit (2). Try again next month.'
            };
        }

        // Get machine info before deleting
        const { data: machine } = await supabase
            .from('user_machines')
            .select('*')
            .eq('user_id', userId)
            .eq('machine_id', machineId)
            .single();

        if (!machine) {
            return { success: false, error: 'Machine not found' };
        }

        // Record deactivation
        await supabase.from('machine_deactivations').insert({
            user_id: userId,
            machine_id: machineId,
            machine_name: machine.machine_name
        });

        // Delete machine registration
        await supabase
            .from('user_machines')
            .delete()
            .eq('id', machine.id);

        return { success: true };

    } catch (error) {
        console.error('Error deactivating machine:', error);
        return { success: false, error: error.message };
    }
};

// Record sign-out and start cooldown
export const recordSignOut = async (userId, machineId) => {
    try {
        await supabase.from('machine_cooldowns').insert({
            user_id: userId,
            machine_id: machineId
        });

        return { success: true };
    } catch (error) {
        console.error('Error recording sign-out:', error);
        return { success: false };
    }
};

// Get user's registered machines
export const getUserMachines = async (userId) => {
    try {
        const { data, error } = await supabase
            .from('user_machines')
            .select('*')
            .eq('user_id', userId)
            .order('last_used_at', { ascending: false });

        if (error) throw error;
        return data || [];

    } catch (error) {
        console.error('Error fetching machines:', error);
        return [];
    }
};

// Get remaining deactivations this month
export const getRemainingDeactivations = async (userId) => {
    try {
        const monthStart = new Date();
        monthStart.setDate(1);
        monthStart.setHours(0, 0, 0, 0);

        const { data } = await supabase
            .from('machine_deactivations')
            .select('*')
            .eq('user_id', userId)
            .gte('deactivated_at', monthStart.toISOString());

        return 2 - (data?.length || 0);

    } catch (error) {
        console.error('Error fetching deactivation count:', error);
        return 0;
    }
};
